import { useNavigate } from 'react-router-dom'
import { useRef, useState, useEffect } from 'react'

// Fades + slides its children in when scrolled into view.
function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null)
  const [show, setShow] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setShow(true); io.disconnect() } })
    }, { threshold: 0.12 })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return <div ref={ref} className={'eh-reveal' + (show ? ' in' : '')} style={{ transitionDelay: `${delay}ms`, ...style }}>{children}</div>
}


const C = {
  bg:       '#FBFBFD',
  white:    '#FFFFFF',
  primary:  '#4F46E5',
  primary2: '#6366F1',
  text:     '#0F172A',
  textSub:  '#475569',
  textMuted:'#94A3B8',
  border:   '#ECEEF2',
  success:  '#10B981',
  amber:    '#F59E0B',
  danger:   '#EF4444',
  shadow:   '0 1px 2px rgba(16,24,40,0.04)',
  shadowMd: '0 2px 8px rgba(16,24,40,0.06)',
  shadowLg: '0 14px 34px rgba(16,24,40,0.08)',
}

const Logo = ({ size = 36 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{ width: size, height: size, borderRadius: size * 0.22, background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ color: '#fff', fontWeight: 800, fontSize: size * 0.38, letterSpacing: '-0.5px' }}>EH</span>
    </div>
    <span style={{ fontWeight: 700, fontSize: 18, color: C.text, letterSpacing: '-0.3px' }}>Employee Hive</span>
  </div>
)

const Btn = ({ children, onClick, style = {}, outline = false }) => (
  <button onClick={onClick} className="eh-btn" style={{
    padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'Inter, system-ui', transition: 'all .15s',
    background: outline ? C.white : C.primary,
    color: outline ? C.text : '#fff',
    border: outline ? `1px solid ${C.border}` : 'none',
    boxShadow: outline ? C.shadow : '0 2px 8px rgba(79,70,229,0.3)',
    ...style,
  }}>{children}</button>
)

const Badge = ({ children, color = C.primary }) => (
  <span style={{ display: 'inline-block', padding: '4px 14px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: `${color}14`, color, border: `1px solid ${color}28`, marginBottom: '1.25rem' }}>
    {children}
  </span>
)

const Card = ({ children, style = {}, className = '' }) => (
  <div className={className} style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: C.shadow, padding: '1.5rem', ...style }}>
    {children}
  </div>
)

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

export default function Landing() {
  const navigate = useNavigate()

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Inter', system-ui, sans-serif", minHeight: '100vh' }}>

      {/* ── Nav ── */}
      <nav style={{ background: C.white, borderBottom: `1px solid ${C.border}`, boxShadow: C.shadow, position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="eh-nav-inner" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 2rem', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Logo />
          <div className="eh-hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            {[
              { label: 'Features', id: 'features' },
              { label: 'How it works', id: 'howitworks' },
              { label: 'Pricing', id: 'pricing' },
            ].map(l => (
              <span key={l.label} onClick={() => scrollTo(l.id)}
                style={{ fontSize: 14, color: C.textSub, cursor: 'pointer', fontWeight: 500 }}>
                {l.label}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <span className="eh-hide-mobile"><Btn outline onClick={() => navigate('/login')}>Sign in</Btn></span>
            <Btn onClick={() => navigate('/signup')}>Get started</Btn>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ background: `linear-gradient(180deg, #F5F7FF 0%, ${C.bg} 55%)`, borderBottom: `1px solid ${C.border}` }}>
        <div className="eh-section-pad" style={{ maxWidth: 1200, margin: '0 auto', padding: '5rem 2rem 4rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem', alignItems: 'center' }}>
          <div>
            <Badge color={C.primary}>Built for small, growing teams 🚀</Badge>
            <h1 className="eh-hero-title" style={{ fontSize: 52, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.5px', margin: '0 0 1.25rem', color: C.text }}>
              Manage your team's{' '}
              <span style={{ color: C.primary }}>performance</span>{' '}
              the right way
            </h1>
            <p style={{ fontSize: 18, color: C.textSub, lineHeight: 1.7, marginBottom: '2rem' }}>
              Employee Hive brings daily check-ins, goal tracking, PDR cycles, monthly meetings and yearly reviews into one clean dashboard — built for small businesses.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              <Btn onClick={() => navigate('/signup')} style={{ fontSize: 16, padding: '13px 32px' }}>
                Start free — no credit card
              </Btn>
            </div>
            <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>
              Free up to 3 employees · No setup fees · Cancel anytime
            </p>
          </div>

          {/* Hero mock */}
          <div className="eh-float" style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: C.shadowLg, overflow: 'hidden' }}>
            <div style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: 5, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: '#fff' }}>EH</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Employee Hive</span>
              </div>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Apex Solutions</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: `1px solid ${C.border}` }}>
              {[
                { label: 'Employees', value: '5', color: C.primary },
                { label: 'Check-ins today', value: '4', color: C.success },
                { label: 'Active goals', value: '12', color: C.amber },
              ].map((s, i) => (
                <div key={s.label} style={{ padding: '14px 16px', borderRight: i < 2 ? `1px solid ${C.border}` : 'none', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Team status today</div>
              {[
                { name: 'Sarah Chen',   role: 'Marketing',   status: 'Checked in', color: C.success },
                { name: 'James Okafor', role: 'Sales',        status: 'Checked in', color: C.success },
                { name: 'Priya Sharma', role: 'Design',       status: 'Pending',    color: C.amber },
                { name: 'Marcus Webb',  role: 'Engineering',  status: 'Checked in', color: C.success },
              ].map(e => (
                <div key={e.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${C.primary}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: C.primary }}>
                      {e.name[0]}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{e.name}</div>
                      <div style={{ fontSize: 10, color: C.textMuted }}>{e.role}</div>
                    </div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: `${e.color}14`, color: e.color }}>
                    {e.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={{ background: C.white, borderBottom: `1px solid ${C.border}`, boxShadow: C.shadow }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 2rem', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem' }}>
          {[
            { value: '5 min', label: 'Average setup time' },
            { value: '₹0',    label: 'To get started' },
            { value: '3 tiers', label: 'Flexible pricing' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: C.primary, letterSpacing: '-0.5px' }}>{s.value}</div>
              <div style={{ fontSize: 14, color: C.textMuted, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{ padding: '5rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <Badge color={C.primary}>EVERYTHING YOU NEED</Badge>
            <h2 className="eh-section-title" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 1rem', color: C.text }}>
              One platform, complete visibility
            </h2>
            <p style={{ fontSize: 17, color: C.textSub, maxWidth: 500, margin: '0 auto' }}>
              From daily standups to yearly reviews — covering the entire performance lifecycle
            </p>
          </div>
          <Reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
            {[
              { icon: '✅', title: 'Daily Check-ins',       desc: 'Employees log what they did, blockers, and mood every day. See your team pulse in real time.',                         color: C.success },
              { icon: '🎯', title: 'PDR Goal Setting',      desc: 'Set weighted goals with achievement plans and review cycles — monthly, quarterly, half-yearly, or yearly.',            color: C.primary },
              { icon: '📎', title: 'Evidence Upload',       desc: 'Attach files, images, PDFs or Google Drive links as proof of goal completion. Everything in one place.',               color: '#8B5CF6' },
              { icon: '🗓', title: 'Monthly Meetings',      desc: 'Structured 1:1 meetings with agenda, notes and action items saved as permanent records.',                              color: C.amber },
              { icon: '⭐', title: 'Performance Reviews',   desc: 'End-of-cycle reviews with self-rating and employer grading. Outstanding to Below Average scale.',                      color: '#EC4899' },
              { icon: '📊', title: 'Reports & Charts',      desc: 'Pie charts, progress bars, trend lines. Generate signed PDF reports after both parties agree.',                        color: '#06B6D4' },
            ].map(f => (
              <Card key={f.title} className="eh-lift">
                <div style={{ width: 44, height: 44, borderRadius: 10, background: `${f.color}14`, border: `1px solid ${f.color}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: '1rem' }}>
                  {f.icon}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: C.textSub, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </Card>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="howitworks" style={{ background: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: '5rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <Badge color={C.success}>HOW IT WORKS</Badge>
            <h2 className="eh-section-title" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 1rem', color: C.text }}>
              Up and running in 5 minutes
            </h2>
            <p style={{ fontSize: 17, color: C.textSub, maxWidth: 500, margin: '0 auto' }}>
              No IT team needed. No training required. Just sign up and go.
            </p>
          </div>
          <Reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 40, maxWidth: 900, margin: '0 auto' }}>
            {[
              { step: '1', title: 'Create your org',    desc: 'Sign up as an employer, name your organization, and choose your plan. Takes under 2 minutes.' },
              { step: '2', title: 'Invite your team',   desc: 'Add employees by email. They get instant access to their personal performance dashboard.' },
              { step: '3', title: 'Track performance',  desc: 'Set goals, run check-ins, hold reviews, and watch your team grow with real data.' },
            ].map(s => (
              <div key={s.step} style={{ textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', boxShadow: '0 4px 12px rgba(79,70,229,0.3)' }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{s.step}</span>
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: C.textSub, lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ padding: '5rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <Badge color={C.amber}>SIMPLE PRICING</Badge>
            <h2 className="eh-section-title" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 1rem', color: C.text }}>
              Start free, scale as you grow
            </h2>
            <p style={{ fontSize: 17, color: C.textSub }}>No hidden fees. No contracts. Cancel anytime.</p>
          </div>
          <Reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, maxWidth: 960, margin: '0 auto' }}>
            {[
              {
                name: 'Free', price: '₹0', period: 'forever', employees: 3, popular: false,
                features: ['Up to 3 employees', 'Daily check-ins', 'Goals + points', 'Basic dashboard'],
                cta: 'Get started free',
              },
              {
                name: 'Seed', price: '₹500', period: '/month', employees: 10, popular: true,
                features: ['Up to 10 employees', 'Everything in Free', 'Monthly meetings', 'Yearly reviews', 'PDR cycles', 'Email support'],
                cta: 'Start Seed plan',
              },
              {
                name: 'Growth', price: '₹1,000', period: '/month', employees: 20, popular: false,
                features: ['Up to 20 employees', 'Everything in Seed', 'PDF report export', 'Priority support', 'Evidence uploads', 'Advanced analytics'],
                cta: 'Start Growth plan',
              },
            ].map(plan => (
              <div key={plan.name} className="eh-lift" style={{
                background: C.white,
                border: `${plan.popular ? 2 : 1}px solid ${plan.popular ? C.primary : C.border}`,
                borderRadius: 16, padding: '2rem', position: 'relative',
                boxShadow: plan.popular ? '0 8px 24px rgba(79,70,229,0.15)' : C.shadow,
              }}>
                {plan.popular && (
                  <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 16px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                    MOST POPULAR
                  </div>
                )}
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>{plan.name}</div>
                <div style={{ fontSize: 13, color: C.textMuted, marginBottom: '1.25rem' }}>Up to {plan.employees} employees</div>
                <div style={{ fontSize: 42, fontWeight: 800, color: plan.popular ? C.primary : C.text, letterSpacing: '-1px', marginBottom: 2 }}>{plan.price}</div>
                <div style={{ fontSize: 14, color: C.textMuted, marginBottom: '1.5rem' }}>{plan.period}</div>
                <div style={{ marginBottom: '1.5rem' }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 14, color: C.textSub, borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ color: C.success, fontWeight: 700, fontSize: 12 }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/signup')} style={{
                  width: '100%', padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Inter, system-ui',
                  background: plan.popular ? `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)` : C.white,
                  color: plan.popular ? '#fff' : C.text,
                  border: plan.popular ? 'none' : `1px solid ${C.border}`,
                  boxShadow: plan.popular ? '0 4px 12px rgba(79,70,229,0.3)' : C.shadow,
                }}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, padding: '5rem 2rem', textAlign: 'center' }}>
        <h2 className="eh-cta-title" style={{ fontSize: 44, fontWeight: 800, color: '#fff', letterSpacing: '-1px', margin: '0 0 1rem' }}>
          Ready to transform your team?
        </h2>
        <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.8)', maxWidth: 480, margin: '0 auto 2.5rem' }}>
          Join small businesses using Employee Hive to build high-performance teams.
        </p>
        <button onClick={() => navigate('/signup')} style={{ padding: '15px 44px', background: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, color: C.primary, cursor: 'pointer', fontFamily: 'Inter, system-ui', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          Get started free today →
        </button>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: '1rem' }}>
          No credit card required · Free forever up to 3 employees
        </p>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: C.white, borderTop: `1px solid ${C.border}`, padding: '1.5rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <Logo size={28} />
          <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>© 2026 Employee Hive · Built for small businesses</p>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {['Privacy', 'Terms', 'Contact'].map(l => (
              <span key={l} style={{ fontSize: 13, color: C.textMuted, cursor: 'pointer' }}>{l}</span>
            ))}
          </div>
        </div>
      </footer>

    </div>
  )
}